// Track IntersectionObservers so they can be disconnected on Turbo re-init,
// preventing stale observers from accumulating across navigations.
import { createInitializeOnce, getCssVariable, getNavOffset, scrollToElement } from './utilities';

const initializeOnce = createInitializeOnce();
const observers = [];
let resizeListenerAdded = false;

// Hide the section content and reveal the "Read more" link, optionally updating its href.
function collapseSection(wrapper, url) {
    const link = wrapper.querySelector('.feature-sections-small__read-more');
    if (link) {
        if (url) link.href = url;
        link.classList.remove('hidden');
    }
    wrapper.querySelector('#turbo_exhibition_feature_section > .section')?.classList.add('hidden-until-read-more');
}

function setupPressHighlightsExpandable() {
    const el = document.querySelector('.press-highlights');
    if (!el) return;

    // Count the number of paragraphs (or other top level elements)
    const itemCount = el.querySelectorAll('.press-highlights__text > *').length;
    const moreButton = el.querySelector('.see-more--press');
    if (!moreButton) return;

    // CSS handles the small-screen-only display; JS only decides if there's enough to expand
    moreButton.classList.toggle('active', itemCount > 3);

    const count = moreButton.querySelector('.see-more__count');
    if (count) count.innerHTML = `&nbsp;(${ itemCount - 3 })`;

    if (!initializeOnce(moreButton)) return;

    moreButton.addEventListener('click', () => {
        el.querySelector('.press-highlights__text').classList.add('active');
        moreButton.remove();
    });
}

function setupFeatureSections() {
    document.querySelectorAll('.feature-sections > li > .feature-section').forEach((section) => {
        if (!initializeOnce(section)) return;

        // Move focus to section content on keyboard-triggered clicks so the
        // user doesn't have to tab through the entire sidebar again.
        section.addEventListener('click', (e) => {
            if (e.detail === 0) {
                section.closest('.feature')?.querySelector('.section')?.focus({ preventScroll: true });
            }
        });

        // Position the hover image popup for artist features
        if (section.closest('.feature--artists')) {
            section.addEventListener('mouseenter', () => {
                const media = section.querySelector('.feature-section__media');
                if (!media) return;

                const rect = (section.querySelector('.feature-section__text') || section).getBoundingClientRect();
                const gap = getCssVariable('--spacing-tiny');

                media.style.top = `${ rect.top - gap }px`;
                media.style.left = `${ rect.left }px`;
                media.style.width = `${ rect.width * 0.5 }px`;
            });
        }
    });
}

// Also see the turbo:frame-render event listener in application.js
export function setupExhibitionFeatures() {
    // Disconnect any previous IntersectionObservers from a prior Turbo render.
    // Old observers reference removed DOM elements and won't fire, but they
    // stay in memory indefinitely without explicit cleanup.
    observers.forEach(o => o.disconnect());
    observers.length = 0;

    // Hydrate feature section templates on large screens only.
    // Images inside <template> are never loaded by the browser,
    // preventing unnecessary downloads on small/medium devices.
    document.querySelectorAll('template[data-feature-sections-template]').forEach((t) => {
        if (window.matchMedia('(min-width: 1000px)').matches) {
            t.replaceWith(t.content);
        }
    });

    // Re-initialize when the viewport crosses into the large breakpoint. This
    // hydrates any feature-sections templates that were skipped on initial
    // (small-screen) load, then re-runs setup so hover/popup handlers attach
    // to the new DOM.
    if (!resizeListenerAdded) {
        resizeListenerAdded = true;
        window.matchMedia('(min-width: 1000px)').addEventListener('change', (e) => {
            if (e.matches) {
                setupExhibitionFeatures();
            }
        });
    }

    setupFeatureSections();
    observers.push(...setupStickyHeader());

    document.querySelectorAll('.feature').forEach((feature) => {
        const select = feature.querySelector('.feature-sections-small__select');
        if (!select || !initializeOnce(select)) return;

        const readMoreLink = feature.querySelector('.feature-sections-small__read-more');
        readMoreLink?.addEventListener('click', () => {
            readMoreLink.classList.add('hidden');
        });

        select.addEventListener('change', ({ target }) => {
            const option = target.selectedOptions[0];
            const index = parseInt(option.value, 10) - 1;

            // Jump carousel to the selected section instantly
            feature.querySelector('.feature-carousel')?._syncToSection?.(index);

            // Update the "Read more" link and hide the section content
            collapseSection(feature, option.dataset.url);
        });
    });
}

// Sync the surrounding chrome (sticky header, sidebar active state, dropdown)
// after the section turbo frame renders, since only the section partial is loaded.
document.addEventListener('turbo:frame-render', (e) => {
    // When the feature frame re-renders (e.g. locale switch), sync carousel
    // data-urls since the carousel sits outside the Turbo frame.
    if (e.target.id === 'turbo_exhibition_feature') {
        const locale = new URLSearchParams(window.location.search).get('language');

        document.querySelectorAll('.feature-carousel .carousel-item[data-url]').forEach((item) => {
            const url = new URL(item.dataset.url, window.location.origin);
            if (locale) {
                url.searchParams.set('language', locale);
            } else {
                url.searchParams.delete('language');
            }
            item.dataset.url = url.pathname + url.search;
        });
    }

    if (e.target.id === 'turbo_exhibition_feature_section') {
        // Update sticky header title from the newly loaded section
        const sectionTitle = e.target.querySelector('.section__title')?.textContent;
        if (sectionTitle) {
            const sticky = document.querySelector('.feature-sticky__inner .body-medium-bold');
            if (sticky) sticky.textContent = sectionTitle;
        }

        // Update sidebar active state
        const sectionParam = new URLSearchParams(window.location.search).get('section') || '1';
        const sectionIndex = parseInt(sectionParam, 10) - 1;
        document.querySelectorAll('.feature-sections > li > .feature-section').forEach((link, i) => {
            link.classList.toggle('active', i === sectionIndex);
        });

        // Update dropdown selected value
        const select = document.querySelector('.feature-sections-small__select');
        if (select) select.value = sectionParam;

        // Hide "Read more" since the Turbo frame just loaded visible content.
        // Without this, switching sections on desktop then resizing to mobile
        // leaves both the "Read more" link and the section content visible.
        const readMoreLink = document.querySelector('.feature-sections-small__read-more');
        if (readMoreLink) readMoreLink.classList.add('hidden');
    }
});

export function setupScrollGradients() {
    document.querySelectorAll('.scroll-gradient').forEach((el) => {
        if (!initializeOnce(el)) return;

        const overlay = document.createElement('div');
        overlay.className = 'scroll-gradient__overlay';
        el.insertAdjacentElement('afterend', overlay);

        let sentinel = el.querySelector('[data-gradient-sentinel]');
        if (!sentinel) {
            sentinel = document.createElement('div');
            sentinel.dataset.gradientSentinel = '';
            sentinel.style.cssText = 'min-height:1px;flex-shrink:0';
            el.appendChild(sentinel);
        }

        const observer = new IntersectionObserver(([entry]) => {
            overlay.classList.toggle('scroll-gradient__overlay--active', !entry.isIntersecting);
        }, { root: el });

        observer.observe(sentinel);
    });
}

function setupStickyHeader() {
    const newObservers = [];

    // Show the sticky header on scroll
    document.querySelectorAll('.feature').forEach((wrapper) => {
        const carousel = wrapper.querySelector('.feature-carousel');
        if (!carousel) return;

        const navOffset = getNavOffset(false);
        const stickyNavHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-nav-height'));

        // Observe the carousel; when it scrolls past the nav bars, show
        // the sticky header.  Uses only pixel rootMargin (percentage values
        // like -100% are unreliable on iOS Safari).
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    wrapper.classList.remove('scrolled');
                } else if (entry.boundingClientRect.top < navOffset + stickyNavHeight) {
                    wrapper.classList.add('scrolled');
                }
            });
        }, { rootMargin: `${ -navOffset }px 0px 0px 0px` });

        observer.observe(carousel);
        newObservers.push(observer);

        // Hide "Read less" when it scrolls past .feature-sticky
        const readLess = wrapper.querySelector('.section__read-less');
        if (readLess) {
            const readLessObserver = new IntersectionObserver(([entry]) => {
                readLess.style.visibility = entry.isIntersecting ? '' : 'hidden';
            }, { rootMargin: `${ -navOffset - stickyNavHeight }px 0px 0px 0px` });
            readLessObserver.observe(readLess);
            newObservers.push(readLessObserver);
        }

        // Click listeners only need binding once — guard against re-entry
        if (!initializeOnce(wrapper)) return;

        // Scroll to top button
        wrapper.querySelector('.feature-sticky button')?.addEventListener('click', (e) => {
            e.preventDefault();
            scrollToElement(wrapper, 'smooth');
        });

        // Read less button
        // Attach listener to the wrapper because the frame DOM can get replaced
        wrapper.addEventListener('click', (e) => {
            if (!e.target.closest('.section__read-less')) return;

            e.preventDefault();
            scrollToElement(wrapper, 'instant');

            // Hide section content and show Read more button
            collapseSection(wrapper);
        });
    });

    return newObservers;
}

export function setupExhibitions() {
    setupExhibitionFeatures();
    setupPressHighlightsExpandable();
}
